import React, { useEffect, useState } from 'react';
import { Alert, Button, Col, Container, Form, Input, InputGroup, InputGroupAddon, Modal, ModalBody, Popover, PopoverBody, PopoverHeader, Row, Table } from 'reactstrap';
import Icon from '../../base/components/Icon';
import LinkOut from '../../base/components/LinkOut';
import PropControl from '../../base/components/PropControl';
import SavePublishDeleteEtc from '../../base/components/SavePublishDeleteEtc';
import GreenTag from '../../base/data/GreenTag';
import KStatus from '../../base/data/KStatus';
import DataStore, { getDataPath } from '../../base/plumbing/DataStore';
import { isTester } from '../../base/Roles';
import { isHTML, isURL, space } from '../../base/utils/miscutils';
import C from '../../C';
import ServerIO from '../../plumbing/ServerIO';
import { setTagPropsFromUser, gdi, saveTag } from './greenTagPageUtils';
import GreenTagExporter from './GreenTagExporter';
import { A } from '../../base/plumbing/glrouter';
import { EMBED_ENDPOINT } from '../../base/utils/pageAnalysisUtils';
import { nonce } from '../../base/data/DataClass';
import DevOnly from '../../base/components/DevOnly';
import Misc from '../MiscOverrides';


/** Utility function to switch creativeHtml to creativeURL */
const fixMisplacedURL = (tag) => ({...tag, creativeURL: tag.creativeHtml, creativeHtml: null });
/** Utility function to switch creativeURL to creativeHtml */
const fixMisplacedHTML = (tag) => ({...tag, creativeHtml: tag.creativeURL, creativeURL: null });


/**
 * Mini form for setting one field on a Green Ad Tag.
 * @param {Object} p
 * @param {GreenTag} p.tag Green Ad Tag to fix
 * @param {string=} p.field Field name to update. Use this or fieldFn.
 * @param {function=} p.fieldFn Takes args (tag, value) and returns an updated copy of tag. Use this or field.
 * @param {function} updateTag Commits the updated tag.
 */
function SetField({tag, field, fieldFn, updateTag}) {
	const [val, setVal] = useState(tag[field] || '');

	const commit = () => {
		if (fieldFn) {
			updateTag(fieldFn(tag, val));
		} else {
			updateTag({...tag, [field]: val});
		}
	};

	return <Form inline className="form-compact">
		<InputGroup>
			<Input style={{flexGrow: 1}} onChange={e => setVal(e.target.value)} />
			<InputGroupAddon addonType="append">
				<Button type="submit" onClick={commit}>Set</Button>
			</InputGroupAddon>
			</InputGroup>
	</Form>;
}


/**
 * Button to perform a quick auto-fix on a Green Ad Tag.
 * @param {Object} p
 * @param {GreenTag} newTag A copy of the tag with the fix applied.
 * @param {function} updateTag Commits the fixed tag.
 * @param {React.Children} children Overrides the button label.
 */
function FixTag({ newTag, updateTag, children, ...props }) {
	return <Button size="sm" onClick={() => updateTag(newTag)} {...props}>{children || 'Fix tag'}</Button>;
}



/**
 * Commit the temporary tags to the server, one at a time.
 * @param {GreenTag[]} tagList List of tags to be saved
 * @param {function} setTagList Updates the tag list as tags are augmented with their saved IDs or error markers
 */
const chainUploadTags = (tagList, setTagList, setUploadingAll) => {
	setUploadingAll(true);
	// This may be a retry - clean up old error messages.
	tagList.forEach(tag => delete tag.error);
	setTagList([...tagList]);

	// Try to upload at rapid intervals - but only start when previous upload is done
	let uploadingOne = false;
	const interval = setInterval(() => {
		if (uploadingOne) return;
		uploadingOne = true;

		// What's the next unprocessed tag? If none, finish uploading.
		const cursor = tagList.findIndex(tag => !tag.id && !tag.error);
		if (cursor < 0) {
			clearInterval(interval);
			setUploadingAll(false);
			return;
		}

		const tag = tagList[cursor];
		// Pass a copy with tag.id = "new" to saveTag to trigger ID generation etc
		const tagAsNew = { ...tag, id: 'new', status: KStatus.DRAFT };

		// Save the tag!
		saveTag(tagAsNew, false).then(savedTag => {
			tagList[cursor] = savedTag; // Replace temp tag with final version in preview table
		}).catch(error => {
			tagList[cursor] = { ...tag, error }; // Or mark with error, if something's gone wrong
		}).always(() => {
			// ...and update the display and clear the way for the next upload.
			setTagList([...tagList]);
			uploadingOne = false;
		});
	}, 50);
};


/**
 * Renders a warning icon with a popover detailing a problem with a particular table cell.
 * @param {Object} p
 * @param {React.Element} message Will be inserted in the popover body.
 */
function CellWarning({ message }) {
	const [popoverId] = useState(() => nonce().replaceAll(/^\d+/g, 'd')); // strip initial numerals for legality as a selector
	const [open, setOpen] = useState();
	const toggle = () => setOpen(!open);

	return <>
		<Button type="button" size="sm" color="outline-warning" className="mr-1" id={popoverId}><Icon name="warning" /></Button>
		<Popover trigger="legacy" isOpen={open} target={popoverId} toggle={toggle}>
			<PopoverHeader>Issue detected</PopoverHeader>
			<PopoverBody>{message}</PopoverBody>
		</Popover>
	</>;
}


/**
 * Check the creatives for a Green Ad Tag and return an Element describing any problems found - with quick-fix controls if possible.
 * @param {GreenTag} tag
 * @param {function} updateTag Commits the quick-fix.
 */
const checkCreatives = (tag, updateTag) => {
	const { creativeURL, creativeHtml } = tag;
	// Creative in wrong field?
	if (isHTML(creativeURL)) {
		return <>
			<div>Detected a HTML tag incorrectly marked as a preview link:<br/><code>{creativeURL}</code></div>
			<FixTag newTag={fixMisplacedHTML(tag)} updateTag={updateTag}>Fix tag</FixTag>
		</>;
	}
	if (isURL(creativeHtml)) {
		return <>
			<div>Detected a preview link incorrectly marked as a HTML tag:<br/><code>{creativeHtml}</code></div>
			<FixTag newTag={fixMisplacedURL(tag)} updateTag={updateTag}>Fix link</FixTag>
		</>;
	}
	// No creative at all?
	if (!creativeURL && !creativeHtml) {
		const fieldFn = (t, val) => isURL(val) ? {...t, creativeURL: val } : { ...t, creativeHtml: val };
		return <>
			<div>No creative provided.<br/>This Green Ad Tag can be created, but will not be fully functional until a creative preview link or HTML tag is provided.</div>
			<SetField fieldFn={fieldFn} tag={tag} updateTag={updateTag} />
		</>;
	}
	// No problems, or couldn't auto-detect creative type
	return null;
};


/**
 * Link a tag's creative in a table cell (including embed links for HTML tags),
 * note detected problems (missing creative, URL entered as HTML, etc) and offer
 * to auto-fix HTML in the URL field or vice versa.
 * @param {Object} p
 * @param {GreenTag} p.tag
 * @param {function} p.updateTag Function which commits changes to this tag back to the main list.
*/
function TableCellCreative({ tag, updateTag }) {
	const [message, setMessage] = useState(null);
	const { creativeURL, creativeHtml } = tag;

	useEffect(() => {
		setMessage(checkCreatives(tag, updateTag));
	}, [creativeURL, creativeHtml]);

	const creativeText = creativeURL || creativeHtml || '(no creative)';

	// Link to the provided creative
	// NB this link will be broken if the creative is in the wrong field for its type
	const creativeLink = creativeURL ? (
		<A href={creativeText}>Preview link</A>
	) : creativeHtml ? (
		<A href={`${EMBED_ENDPOINT}?why=measure-tag&tag=${encodeURIComponent(creativeText)}`}>Embed tag</A>
	) : (
		<code>{creativeText}</code>
	);

	return <td title={creativeText}>
		{message && <CellWarning message={message} />}
		{creativeLink}
	</td>;
}


/**
 * Display one green tag as a table row. Fetch agency/brand/campaign to display name instead of ID.
 * @param {Object} p
 * @param {GreenTag} p.tag
 * @param {function} p.updateTag Function which commits changes to this tag back to the main list.
 */
function TagTableRow({ tag, updateTag }) {
	const agency = gdi(C.TYPES.Agency, tag.agencyId)?.value;
	const vertiser = gdi(C.TYPES.Advertiser, tag.vertiser)?.value;
	const campaign = gdi(C.TYPES.Campaign, tag.campaign, tag.campaign)?.value;

	// Flag name and format problems
	const nameOK = tag.name && !tag.name.match(/upload row \d/);
	const nameMessage = nameOK ? null : (
		<div>
			{tag.name ? <>
				This tag has a placeholder name which should be replaced before saving.
			</> : <>
				Green Ad Tags cannot be created without a name.
			</>}<br/>
			<SetField tag={tag} updateTag={updateTag} field="name" />
	</div>
	);

	const format = tag.format || '(no format)';
	const formatMessage = tag.format ? null : (
		<div>
			Tags can be created without a format, but will not be fully functional until it is set.<br/>
			<FixTag newTag={{...tag, format: 'display'}} updateTag={updateTag}>Display</FixTag>
			<FixTag className="ml-1" newTag={{...tag, format: 'video'}} updateTag={updateTag}>Video</FixTag>
			<FixTag className="ml-1" newTag={{...tag, format: 'social'}} updateTag={updateTag}>Social</FixTag>
		</div>
	);

	return <>
		<tr>
			<th>{tag.id ? '☑️' : tag.error ? '❌' : ''}</th>
			<th title={tag.name}>
				{nameMessage && <CellWarning message={nameMessage} />}
				{tag.name}
			</th>
			<th title={format}>
				{formatMessage && <CellWarning message={formatMessage} />}
				{format}
			</th>
			<TableCellCreative tag={tag} updateTag={updateTag} />
			<td title={agency?.name}>{agency?.name}</td>
			<td title={vertiser?.name}>{vertiser?.name}</td>
			<td title={campaign?.name}>{campaign?.name}</td>
		</tr>
		{tag.error && <tr><td colSpan="7" className="text-danger">{tag.error}</td></tr>}
	</>;
}


/**
 * Generate a "fix-all button" spec to fill in all unformatted tags.
 * @param {string} format The target format - "display", "video", "social"
 * @param {function} setTags A useState setter, which accepts a transform function for a value
 */
const formatFix = (format, setTags) => {
	const fixFn = () => setTags(tags => tags.map(t => t.format ? t : { ...t, format }));
	return { label: `Set missing to ${format}`, doIt: fixFn };
};


/**
 * Generate a digest of issues with a list of Green Ad Tags - and quick fixes, if possible
 * @param {Object} p
 * @param {GreenTag[]} p.tags List of Green Ad Tags to evaluate
 * @param {function} p.setTags Updates the list of tags
 */
const checkTagProblems = (tags, setTags) => {
	const problems = {};

	tags.forEach((tag) => {
		if (!tag.name) { // Missing name?
			if (!problems.noName) problems.noName = { problem: 'no name', count: 0 };
			problems.noName.count += 1;
		}
		
		if (!tag.format) { // Missing format?
			if (!problems.noFormat) {
				// Are the tags which do have formats all the same?
				const restFormat = tags.filter(t => t.format).reduce((acc, t) => {
					return (acc === null || acc === t.format) ? t.format : false;
				}, null);
				// If so - change missing items to that format, if not - offer both formats
				let fixes = restFormat ? [formatFix(restFormat, setTags)] : [
					formatFix('display', setTags), formatFix('video', setTags), formatFix('social', setTags)
				];
				problems.noFormat = { problem: 'no format set', count: 0, fixes };
			}
			problems.noFormat.count += 1;
		}

		if (!tag.creativeURL && !tag.creativeHtml) { // No creative at all?
			if (!problems.noURL) problems.noURL = { problem: 'no creative attached', count: 0 };
			problems.noURL.count += 1;
		}

		if (tag.creativeURL) { // URL in the HTML field?
			if (isHTML(tag.creativeURL)) {
				if (!problems.misplacedHTML) {
					problems.misplacedHTML = {
						problem: 'a creative HTML tag entered as a preview link',
						count: 0,
						// Auto-fix by swapping all misplaced URLs to the HTML field
						fixes: [{
							label: 'Convert to HTML tag(s)',
							doIt: () => setTags(tags.map(t => isHTML(t.creativeURL) ? fixMisplacedHTML(t) : t)),
						}]
					};
				}
				problems.misplacedHTML.count += 1;
			} else if (!isURL(tag.creativeURL)) { // Creative URL that doesn't appear to be a URL?
				if (!problems.badURL) problems.badURL = { problem: 'a malformed creative preview link', count: 0 };
				problems.badURL.count += 1;
			}
		}

		if (tag.creativeHtml) { // HTML in the URL field?
			if (isURL(tag.creativeHtml)) {
				if (!problems.misplacedURL) {
					problems.misplacedURL = {
						problem: 'a creative preview link entered as a HTML tag',
						count: 0,
						// Auto-fix by swapping all misplaced HTML tags to the URL field
						fixes: [{
							label: 'Convert to preview link(s)',
							doIt: () => setTags(tags.map(t => isURL(t.creativeHtml) ? fixMisplacedURL(t) : t)),
						}]
					};
				}
				problems.misplacedURL.count += 1;
			} else if (!isHTML(tag.creativeHtml)) { // HTML creative that doesn't appear to be HTML?
				if (!problems.badHTML) problems.badHTML = { problem: 'a malformed creative HTML tag', count: 0 };
				problems.badHTML.count += 1;
			}
		}
	});

	return problems;
};


/**
 * Display one issue found by checkTagProblems & buttons to auto-fix, if applicable.
 * @param {Object} p
 * @param {number} p.count Number of tags exhibiting this class of problem
 * @param {(React.Element|string)} p.problem Describes the issue - will be prefixed as "{N} tag(s) has/have {problem}"
 * @param {Object[]} p.fixes List of actions which can be taken on all affected tags - each has "label" (button label) and "doIt" (execute function)
 */
function ProblemWarning({ count, problem, fixes }) {
	const prefix = (count === 1) ? 'One tag has' : `${count} tags have`;

	return <Alert color="warning" className="p-2 mb-1">
		{prefix} {problem}.
		{fixes?.map(({label, doIt}, i) => <Button key={`fix-${i}`} size="sm" className="ml-1" onClick={doIt}>{label}</Button>)}
	</Alert>;
}


/**
 * Show progress through the chain-upload process
 * @param {Object} p
 * @param {GreenTag[]} p.previewTags Successful uploads will have tag.id, failed ones tag.error
 * @returns {React.Element}
 */
function UploadStatus({previewTags}) {
	const progress = previewTags.reduce((acc, tag) => (tag.id || tag.error) ? acc + 1 : acc, 0);
	return <Misc.Loading inline text={`Uploading... (${progress}/${previewTags.length})`} />;
}


/**
 * Inform the user of a successful bulk upload & allow them to reset the form
 * @param {Object} p
 * @param {GreenTag[]} p.previewTags Used to show count
 * @param {Function} p.setPreviewTags Used to clear the upload list
 * @returns {React.Element}
 */
function UploadSuccess({previewTags, setPreviewTags}) {
	return <>
		<Alert color="success" className="flex-row align-items-center">
			<strong>All {previewTags.length} tags from this CSV have been successfully created.</strong>
			<Button color="primary" className="ml-4" onClick={() => setPreviewTags(null)}>OK</Button>
		</Alert>
		<GreenTagExporter tags={previewTags} />
	</>;
}


/**
 * Show tags which have been extracted from a CSV - but not necessarily persisted yet - in a table.
 * @param {Object} p
 * @param {GreenTag[]} p.previewTags
 * @param {Function} p.setPreviewTags Used by the auto-fix functions after making edits
 * @returns {React.Element}
 */
function PreviewTable({previewTags, setPreviewTags}) {
	// Allow preview rows to edit their tags and trigger a redraw
	const updateTag = (newTag, i) => setPreviewTags(oldTags => oldTags.toSpliced(i, 1, newTag));
	// Header row contents
	const headers = [<Icon name="outtray" />, 'Name', 'Type', 'Creative', 'Agency', 'Brand', 'Campaign'];

	return <Table bordered striped className="preview-table mt-2">
		<thead>
			<tr>{headers.map(h => <th>{h}</th>)}</tr>
		</thead>
		<tbody>
			{previewTags.map((tag, i) => <TagTableRow tag={tag} key={i} updateTag={newTag => updateTag(newTag, i)} />)}
		</tbody>
	</Table>;
}


/**
 * Preview the tags contained in an uploaded CSV, report any problems, and show controls to bulk-create them.
 * @param {Object} p
 * @param {GreenTag[]} p.previewTags List of not-yet-saved tags generated from an uploaded CSV.
 * @param {function} p.setPreviewTags Replaces the tags list and redraws the component
 * @returns {React.Element}
 */
function BulkUploadPreview({ previewTags, setPreviewTags }) {
	if (!previewTags.length) return null;

	// Track status of chain-upload
	const [uploadingAll, setUploadingAll] = useState();

	// Data issues with tags, rather than upload errors) - like misplaced creative HTML/URL
	const [problems, setProblems] = useState([]);
	useEffect(() => {
		const tagProblems = checkTagProblems(previewTags, setPreviewTags);
		setProblems(Object.values(tagProblems));
	}, [previewTags]);

	// Have all tags been successfully committed? Has there been an attempt where some failed?
	const hasErrors = previewTags.reduce((acc, tag) => acc || (tag.error), false);
	const allOK = previewTags.reduce((acc, tag) => acc && (tag.id), true);

	// Start uploading tags on button click (TODO return a promise?)
	const doIt = () => chainUploadTags(previewTags, setPreviewTags, setUploadingAll);

	// Don't show "create tags" button or problem digest until all tags have been attempted
	const canUpload = (!uploadingAll && !allOK);
	const showProblems = (!uploadingAll && !!problems.length);

	return <>
		<h3>Preview of your uploaded tags</h3>
		{allOK && <UploadSuccess previewTags={previewTags} setPreviewTags={setPreviewTags}/> }
		{uploadingAll && <UploadStatus previewTags={previewTags} />}
		{canUpload && <Button color="default" onClick={doIt}>{hasErrors ? 'Retry failed tags' : 'Create All'}</Button>}
		{showProblems && <div className="mt-2">
			{problems.map((p, i) => <ProblemWarning key={`problem-warning-${i}`} {...p} />)}
		</div>}
		<PreviewTable previewTags={previewTags} setPreviewTags={setPreviewTags} />
	</>;
}


/**
 * A modal dialog to upload a CSV of Green Ad Tags, inspect them for problems, and save them to the server.
 */
function BulkUploader() {
	const [open, setOpen] = useState();
	const toggle = () => setOpen(!open);
	// Generated-from-CSV tags returned by GreenTagServlet
	const [previewTags, setPreviewTags] = useState();
	const [uploadWarnings, setUploadWarnings] = useState();

	// Create a template tag to fill in common fields - agency, vertiser, campaign
	const path = DataStore.getDataPath({ type: C.TYPES.GreenTag, status: KStatus.DRAFT, id: 'bulkTemplate' });
	let templateTag = DataStore.getValue(path);
	if (!templateTag) templateTag = new GreenTag();
	// try to autofill agency and brand from user's shares
	useEffect(() => {
		setTagPropsFromUser(templateTag);
		DataStore.setValue(path, templateTag);
	}, [templateTag.agencyId, templateTag.vertiser]);


	// Don't show controls until initial template setup is complete
	const button = <Button color="default" onClick={toggle}>Bulk upload tags</Button>;
	if (!templateTag) return button;

	// Don't allow upload until the critical values for the template are filled out.
	const { agencyId, vertiser: vertiserId, campaign: campaignId } = DataStore.getValue(path) || {};
	const agency = agencyId && gdi(C.TYPES.Agency, agencyId)?.value;
	const vertiser = vertiserId && gdi(C.TYPES.Advertiser, vertiserId)?.value;
	const campaign = campaignId && gdi(C.TYPES.Campaign, campaignId)?.value;
	const cpath = campaign && getDataPath({type:C.TYPES.Campaign, id:campaignId, status:KStatus.DRAFT});

	const readyToUpload = agency && vertiser && campaign;

	// Handle response from GreenTagServlet, including warnings
	const onUpload = ({ response }) => {
		setPreviewTags(response?.cargo?.items);
		setUploadWarnings(response?.messages?.map(msg => msg.text));
	};

	return <>
		{button}
		<Modal id="green-bulk-uploader" isOpen={open} toggle={toggle} size="xl">
			<ModalBody>
				<Form action="/greenTag">
					<h3>Bulk Tag Uploader</h3>
					<p>Upload a CSV to generate Green Ad Tags in bulk. Use <LinkOut href="/data/Green-Ad-Tag-bulk-upload.csv">this spreadsheet</LinkOut> as a template, making sure to keep the header columns unchanged.</p>
					<DevOnly>
						Additional spreadsheets for testing:{' '}
						<LinkOut href="/data/Green-Ad-Tag-bulk-upload-creative-test-cases.csv">Misc errors (one of each)</LinkOut>,{' '}
						<LinkOut href="/data/Green-Ad-Tag-bulk-upload-creative-test-cases-multiples.csv">Misc errors (multiple)</LinkOut>,{' '}
						<LinkOut href="/data/Green-Ad-Tag-bulk-upload-creative-test-cases-canfixformat.csv">Auto-fillable missing formats</LinkOut>
					</DevOnly>
					<p>
						The <code>name</code> column should be a readable, recognisable name for the tag.<br />
						Either the <code>creativeURL</code> or <code>creativeHtml</code> column should be filled for each row. (They can be mixed within the same CSV file.)<br/>
						The <code>creativeURL</code> column takes a preview, VAST, or upload (eg WeTransfer) link for your creative.<br/>
						The <code>creativeHtml</code> column takes a HTML tag or snippet for your creative, such as your DSP might export.
					</p>
					<p>You'll have the opportunity to review the tags before saving them.</p>
					<p>First, set the agency, brand, and campaign for your uploaded tags:</p>
					<Container>
						<Row>
							<Col xs="12" sm="3">
								<PropControl label="Agency" path={path} prop="agencyId" type="DataItem" pageSize={10} itemType={C.TYPES.Agency} required
									status={KStatus.PUBLISHED} readOnly={!isTester()} notALink={!isTester()}
								/>
							</Col>
							<Col xs="12" sm="3">
								<PropControl label="Brand" path={path} prop="vertiser" type="DataItem" pageSize={10} itemType={C.TYPES.Advertiser} required
									status={KStatus.PUB_OR_DRAFT} canCreate base={{ agencyId: templateTag.agencyId }} createProp="name"
									notALink={!isTester()}
								/>
							</Col>
							<Col xs="12" sm="3">
								<PropControl label="Campaign" path={path} prop="campaign" type="DataItem" pageSize={10} itemType={C.TYPES.Campaign} required
									status={KStatus.PUB_OR_DRAFT}
									disabled={!templateTag.vertiser}
									canCreate base={{ agencyId: templateTag.agencyId, vertiser: templateTag.vertiser }} createProp="name"
									notALink={!isTester()}
									help="Use a common campaign ID to collate logging across multiple tags."
								/>
							</Col>
							<Col xs="12" sm="3">
								<PropControl label="Your campaign reference" disabled={!cpath} path={cpath} prop="xref"
									help={space(!cpath && 'First set a Campaign', 'Optional. Use this e.g. to record a link with an account or a deal in your CRM')}
								/>
								{cpath && <SavePublishDeleteEtc autoSave autoPublish hidden sendDiff id={campaignId} type={C.TYPES.Campaign} />}
							</Col>
						</Row>
					</Container>
					<PropControl type="spreadsheetUpload" prop="bulkTags" label={readyToUpload ? 'Upload your CSV file:' : 'Please set agency, brand and campaign before uploading.'}
						endpoint={ServerIO.getEndpointForType(C.TYPES.GreenTag)}
						uploadParams={{
							action: 'bulkUpload',
							item: JSON.stringify(templateTag)
						}}
						onUpload={onUpload}
						disabled={!readyToUpload}
					/>
					{(uploadWarnings?.length) ? <>
						Some issues were encountered while processing your uploaded CSV:
						{uploadWarnings.map((warning, i) => {
							const dismissFn = () => setUploadWarnings(uploadWarnings.toSpliced(i, 1));
							return <Alert color="warning" className="p-2 mb-1">
								{warning} <Button size="sm" onClick={dismissFn}>OK</Button>
							</Alert>;
						})}
					</> : null}
					{previewTags ? (
						<BulkUploadPreview previewTags={previewTags} setPreviewTags={setPreviewTags} />
					) : null}
				</Form>
			</ModalBody>
		</Modal>
	</>;
}

export default BulkUploader;
